// 작성 페이지 탭 — 작성폼 미리보기를 실제 작성 화면과 동일한 레이아웃 셸로 감싼다.
//
// 실제 작성 화면(web new-composed)과 **같은 ComposeWritePageShell을 공유**해 비주얼이 구성상
// 동일(parity by construction)하다 — 복제 드리프트 0. 여기선 디바이스 해상도(활성 뷰) 기반 최소
// 높이를 줘서 하단 컨트롤 바가 페이지 하단에 배치되게 하고, 헤더/하단 바를 슬롯 선택(구성 진입)으로 만든다.
//
// 인터랙션: 위젯처럼 "선택 → 우측 속성창에 그 슬롯 구성". 헤더/하단 바 클릭 → 그 슬롯 선택(위젯 선택
// 해제). 버튼도 그 슬롯을 선택한다(실제 제출/미리보기/취소는 실화면에서). chrome은 페이지 틀이라
// 좌측 위젯 트리(폼 콘텐츠 전용)에는 넣지 않는다.

import type { ReactNode } from 'react';
import { ComposeWritePageShell } from '@safetyhub/report-composer';
import { COMPOSER_VIEWS } from '@safetyhub/shared';
import { Card } from '@safetyhub/ui';
import type {
  ComposeChromeConfig,
  ComposeChromeSlot,
  ComposeMode,
  ComposeSubmitSituation,
} from '@safetyhub/shared';

/** 활성 뷰의 대표 디바이스 높이(해상도 기반 페이지 프레임 최소 높이). 없으면 기본값. */
function viewFrameMinHeight(activeView: string): number {
  const view = COMPOSER_VIEWS.find((v) => v.key === activeView);
  return view?.devices?.[0]?.height ?? 640;
}

export function ComposeChromePreviewFrame({
  config,
  title,
  subtitle,
  activeView,
  mode,
  submitSituation,
  selectedSlot,
  onSelectSlot,
  children,
}: {
  /** 템플릿 composeChrome(미지정이면 기본 세트 렌더 — 실제 작성 화면과 동일). */
  config?: ComposeChromeConfig;
  /** 헤더 대제목(예: "안전관리상태보고서 작성"). */
  title: string;
  /** 헤더 소제목(현장명 등). */
  subtitle?: string;
  /** 활성 반응형 뷰키(작성 탭 뷰 스위처와 공유) — 뷰별 해소 + 프레임 높이. */
  activeView: string;
  /** 작성 모드(create/edit) 시뮬레이션 — chrome 항목 visibleIn 해소. 상황에서 파생된다. */
  mode: ComposeMode;
  /**
   * submit 버튼 상황(분할 진행·이어질 흐름 CTA) 시뮬레이션 — 미지정이면 셸이 `{ mode }`로
   * 접어 현행 모드별 기본 라벨 그대로다. 라벨 해소는 셸(=실화면과 같은 코드)이 하므로 여기선
   * 상황만 넘긴다. 결정 2026-09-06 §3.
   */
  submitSituation?: ComposeSubmitSituation;
  /** 현재 선택된 chrome 슬롯(선택 하이라이트). */
  selectedSlot: ComposeChromeSlot | null;
  /** 헤더/하단 바 클릭 시 그 슬롯 선택(우측 속성창에 구성 표시). */
  onSelectSlot: (slot: ComposeChromeSlot) => void;
  /** 감쌀 작성폼 미리보기 캔버스. */
  children: ReactNode;
}) {
  return (
    <Card padding="none" className="overflow-hidden">
      <ComposeWritePageShell
        config={config}
        activeView={activeView}
        mode={mode}
        submitSituation={submitSituation}
        title={title}
        subtitle={subtitle}
        // 미리보기 버튼 — 실제 동작 대신 그 슬롯 선택(구성 진입).
        getButtonProps={(_item, slot) => ({ onClick: () => onSelectSlot(slot) })}
        headerInteractive={{
          selected: selectedSlot === 'header',
          onSelect: () => onSelectSlot('header'),
        }}
        footerInteractive={{
          selected: selectedSlot === 'actionBar',
          onSelect: () => onSelectSlot('actionBar'),
        }}
        showSlotAffordance
        minHeight={viewFrameMinHeight(activeView)}
      >
        {children}
      </ComposeWritePageShell>
    </Card>
  );
}
